import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteLayout } from "@/layouts/site-layout";
import { SEO } from "@/components/SEO";

const Book = () => {
  const processSteps = [
    { day: "Saturday", title: "Kickoff" },
    { day: "Sunday", title: "Scope Freeze" },
    { day: "Mon–Fri", title: "Build" },
    { day: "Saturday", title: "Polish" },
    { day: "Sunday", title: "Launch" }
  ];

  const buildTypes = [
    "AI SaaS Tools",
    "Chatbots & Assistants",
    "Marketplaces",
    "Subscription Apps",
    "Lead-Gen Tools"
  ];

  const addOns = [
    "Launch Video",
    "Product Hunt Kit",
    "Lead-Gen Tool",
    "Post-Launch Support"
  ];

  const topFaqs = [
    { q: "How many projects per week?", a: "4" },
    { q: "Can I change scope midweek?", a: "No — scope freezes Sunday" },
    { q: "What stack?", a: "Modern frameworks (Next.js, Node, etc.)" },
    { q: "Who owns the code?", a: "You — 100%" },
    { q: "Refund policy?", a: "Before scope freeze only" },
    { q: "Support included?", a: "30 days post-launch" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ReservationService",
    "name": "Book Your MVP Build Week",
    "description": "Reserve your seat for our one week MVP launch sprint. Launch your MVP in 7 days.",
    "provider": {
      "@type": "Organization",
      "name": "OneWeekMVPs"
    }
  };

  return (
    <SiteLayout>
      <SEO
        title="Book Your MVP Launch Week | Reserve Your 7-Day MVP Sprint"
        description="Reserve your seat for this week's MVP launch sprint. Book your launch week and go live by Sunday. Only 4 seats available per week. $1,999 flat rate for full MVP development and launch in 7 days. Limited time: 50% off - now $999."
        keywords="book MVP launch, reserve MVP launch week, MVP launch sprint booking, 7-day MVP development booking, book one week MVP launch"
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://oneweekmvps.com'}/book`}
        structuredData={structuredData}
      />
      {/* Video Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h1 className="text-5xl md:text-7xl font-heading leading-tight">
                  Launch your MVP in <span className="text-primary">7 days</span>
                </h1>
                <p className="text-xl text-muted-foreground">
                  Watch how we help founders go from idea to real users in one week.
                </p>
                <Button size="lg" className="text-base px-8" asChild>
                  <a href="https://cal.com/buildwithrishi/15min">Book Your Seat</a>
                </Button>
              </div>

              {/* Video Placeholder */}
              <div className="relative aspect-video bg-card border border-border/50 rounded-lg overflow-hidden flex items-center justify-center">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <div className="w-0 h-0 border-l-8 border-l-primary border-t-6 border-t-transparent border-b-6 border-b-transparent ml-1"></div>
                  </div>
                  <p className="text-muted-foreground">90-120 second explainer video</p>
                </div>
                <div className="absolute bottom-4 left-4">
                  <Button variant="outline" size="sm">
                    See How It Works
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof & Metrics */}
      <section className="py-12 border-y border-border/50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-heading text-primary mb-2">100+</div>
                <div className="text-sm text-muted-foreground">MVPs Built</div>
              </div>
              <div>
                <div className="text-3xl font-heading text-primary mb-2">7 Days</div>
                <div className="text-sm text-muted-foreground">Delivery Time</div>
              </div>
              <div>
                <div className="text-3xl font-heading text-primary mb-2">$7.5K</div>
                <div className="text-sm text-muted-foreground">Flat Rate</div>
              </div>
              <div>
                <div className="text-3xl font-heading text-primary mb-2">12+</div>
                <div className="text-sm text-muted-foreground">Countries</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-4xl md:text-5xl font-heading">
              We started because ideas were <span className="text-primary">dying in to-do lists</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Founders waste months on half-built products. We built OneWeekMVPs to change that — fast sprints, real launches, no excuses.
            </p>
          </div>
        </div>
      </section>

      {/* 7-Day Process */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                The <span className="text-primary">7-Day Process</span>
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {processSteps.map((step, idx) => (
                <div key={idx} className="flex items-center">
                  <Card className="border-border/50">
                    <CardContent className="p-6 text-center">
                      <div className="text-sm text-primary font-medium mb-1">{step.day}</div>
                      <div className="font-heading text-lg">{step.title}</div>
                    </CardContent>
                  </Card>
                  {idx < processSteps.length - 1 && (
                    <div className="hidden md:block text-2xl text-muted-foreground mx-2">→</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What You Can Build */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                What you can <span className="text-primary">build</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {buildTypes.map((type, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardContent className="p-6 text-center">
                    <p className="font-medium">{type}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Snapshot */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <Card className="border-border/50">
              <CardContent className="p-12 text-center space-y-6">
                <Badge className="mb-4">Our Flagship Package</Badge>
                <h2 className="text-4xl md:text-5xl font-heading">
                  <div className="space-y-1">
                    <div className="text-muted-foreground line-through text-2xl">$1,999</div>
                    <div>One Week MVP — <span className="text-primary text-4xl">$999</span></div>
                    <div className="text-primary text-sm font-medium">50% OFF Limited Time</div>
                  </div>
                </h2>
                <p className="text-lg text-muted-foreground">
                  Auth • Payments • AI • Dashboard • Deployment • 30 Days Support
                </p>
                <Button size="lg" className="text-base px-8" asChild>
                  <a href="https://checkout.dodopayments.com/buy/pdt_EGEdTJ9NOLaMWTDmwqhbv?session=sess_HIaYqixlwI">Reserve Your Seat</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Add-Ons & <span className="text-primary">Launch Extras</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {addOns.map((addon, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm">{addon}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Top 6 FAQs */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                <span className="text-primary">Top Questions</span>
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {topFaqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`}>
                  <AccordionTrigger className="text-left text-lg">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Calendly Booking CTA */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
              Ready to <span className="text-primary">launch this week?</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Book a 15-minute call. We'll confirm availability and your Saturday kickoff slot.
            </p>

            {/* Calendly Placeholder */}
            <Card className="border-border/50">
              <CardContent className="p-12">
                <div className="aspect-video bg-background/50 rounded-lg flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <p className="text-muted-foreground">Calendly Widget Embedded Here</p>
                    <Button size="lg" className="text-base px-8" asChild>
                      <a href="https://cal.com/buildwithrishi/15min">Book a Call</a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground">
              After booking, we'll send a short prep form so your kickoff is focused and efficient.
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Book;
